/**
 * Creates URLSearchParams from a params object, optionally merging with existing query string.
 *
 * @param params - Object containing key-value pairs to convert to query parameters.
 *                 Arrays are appended as multiple values for the same key.
 *                 Undefined and null values are skipped.
 * @param currentSearchString - Optional existing query string to merge with new params.
 *                              Can include or omit the leading "?".
 *                              Existing params will be preserved and new params will be appended.
 * @returns URLSearchParams object that can be used with fetch or converted to string.
 *
 * @example
 * // Basic usage
 * createQueryParams({ name: 'John', age: 30 })
 * // Returns: URLSearchParams with "name=John&age=30"
 *
 * @example
 * // Merging with existing query string
 * createQueryParams({ page: 2 }, '?sort=asc&filter=active')
 * // Returns: URLSearchParams with "sort=asc&filter=active&page=2"
 *
 * @example
 * // Array values
 * createQueryParams({ tags: ['tech', 'web'] })
 * // Returns: URLSearchParams with "tags=tech&tags=web"
 */
export function createQueryParams(params: Record<string, unknown>, currentSearchString?: string) {
  const queryParams = new URLSearchParams(currentSearchString);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value?.forEach(f => queryParams.append(key, f));
      } else {
        queryParams.append(key, String(value));
      }
    }
  });

  return queryParams;
}
